/**
 * Pure configuration for the customizable Reports surface: widget ids, their
 * filter scope, full-bleed rendering, layout normalization and scope detection.
 *
 * This module imports nothing from the registry, tiles or the reports slice so
 * it can be used by the slice (for normalization) and the registry alike
 * without an import cycle.
 */
import { normalizeFullBleedRows } from '~/components/customize/layoutHelpers'
import type { DashboardLayout, FilterScope, Row, WidgetInstance } from '~/components/customize/types'

import type { ReportType } from '../../util'

// --- Energy (site-scope) widget ids ---
export const ENERGY_OVERVIEW_WIDGET_ID = 'energy-overview'
export const ENERGY_BY_SITE_WIDGET_ID = 'energy-by-site'
export const ENERGY_BY_SITE_COMPARISON_WIDGET_ID = 'energy-by-site-comparison'
export const ENERGY_PERIOD_COMPARISON_TABLE_WIDGET_ID = 'energy-period-comparison-table'
export const ENERGY_PERIOD_SUMMARY_WIDGET_ID = 'energy-period-summary'
export const ENERGY_USAGE_BY_SITE_TABLE_WIDGET_ID = 'energy-usage-by-site-table'
export const ENERGY_OVER_TIME_WIDGET_ID = 'energy-over-time'
export const ENERGY_IMPORTED_HISTOGRAM_WIDGET_ID = 'energy-imported-histogram'
export const TRANSACTIONS_OVER_TIME_WIDGET_ID = 'transactions-over-time'
export const TRANSACTION_COUNT_HISTOGRAM_WIDGET_ID = 'transaction-count-histogram'
export const ENERGY_VS_TRANSACTIONS_WIDGET_ID = 'energy-vs-transactions'
export const CHARGER_STATUS_SUMMARY_WIDGET_ID = 'charger-status-summary'
export const CONNECTOR_STATUS_SUMMARY_WIDGET_ID = 'connector-status-summary'
/** @deprecated Migrated to charger-status-summary + connector-status-summary on load. */
export const HISTORICAL_STATUS_SUMMARY_WIDGET_ID = 'historical-status-summary'
export const CHARGER_METRICS_HEATMAP_WIDGET_ID = 'charger-metrics-heatmap'

// --- Vehicle (org-scope) widget ids ---
export const VEHICLE_OVERVIEW_WIDGET_ID = 'vehicle-overview'
export const VEHICLE_DISTANCE_HISTOGRAM_WIDGET_ID = 'vehicle-distance-histogram'
export const VEHICLE_EFFICIENCY_HISTOGRAM_WIDGET_ID = 'vehicle-efficiency-histogram'
export const VEHICLE_TRIP_COUNT_HISTOGRAM_WIDGET_ID = 'vehicle-trip-count-histogram'

// --- Generic raw-results table (one tile per scope; same component) ---
export const RAW_RESULTS_TABLE_SITE_WIDGET_ID = 'raw-results-table-site'
export const RAW_RESULTS_TABLE_ORG_WIDGET_ID = 'raw-results-table-org'

/**
 * The filter scope each report widget supports. Energy/charger widgets accept
 * organization + site filters; vehicle widgets accept organization only (a
 * backend limitation — the vehicle metrics endpoints take `organizationId`
 * but not `siteId`). A report's scope must be homogeneous, so the widget
 * picker only offers widgets matching the active report's scope.
 */
export const REPORT_WIDGET_SCOPE: Record<string, FilterScope> = {
  [ENERGY_OVERVIEW_WIDGET_ID]: 'site',
  [ENERGY_BY_SITE_WIDGET_ID]: 'site',
  [ENERGY_BY_SITE_COMPARISON_WIDGET_ID]: 'site',
  [ENERGY_PERIOD_COMPARISON_TABLE_WIDGET_ID]: 'site',
  [ENERGY_PERIOD_SUMMARY_WIDGET_ID]: 'site',
  [ENERGY_USAGE_BY_SITE_TABLE_WIDGET_ID]: 'site',
  [ENERGY_OVER_TIME_WIDGET_ID]: 'site',
  [ENERGY_IMPORTED_HISTOGRAM_WIDGET_ID]: 'site',
  [TRANSACTIONS_OVER_TIME_WIDGET_ID]: 'site',
  [TRANSACTION_COUNT_HISTOGRAM_WIDGET_ID]: 'site',
  [ENERGY_VS_TRANSACTIONS_WIDGET_ID]: 'site',
  [CHARGER_STATUS_SUMMARY_WIDGET_ID]: 'site',
  [CONNECTOR_STATUS_SUMMARY_WIDGET_ID]: 'site',
  [HISTORICAL_STATUS_SUMMARY_WIDGET_ID]: 'site',
  [CHARGER_METRICS_HEATMAP_WIDGET_ID]: 'site',
  [RAW_RESULTS_TABLE_SITE_WIDGET_ID]: 'site',
  [VEHICLE_OVERVIEW_WIDGET_ID]: 'org',
  [VEHICLE_DISTANCE_HISTOGRAM_WIDGET_ID]: 'org',
  [VEHICLE_EFFICIENCY_HISTOGRAM_WIDGET_ID]: 'org',
  [VEHICLE_TRIP_COUNT_HISTOGRAM_WIDGET_ID]: 'org',
  [RAW_RESULTS_TABLE_ORG_WIDGET_ID]: 'org'
}

export function getReportWidgetScope(widgetId: string): FilterScope | undefined {
  return REPORT_WIDGET_SCOPE[widgetId]
}

// --- Data Table tile: the Report Builder's report types, folded in ---

/**
 * The report datasets a Data Table tile can show, grouped by the tile's filter
 * scope. The legacy Report Builder's report types are folded into this tile —
 * a `'site'`-scope tile offers the charger/transaction datasets, an `'org'`
 * tile offers the vehicle datasets. The first entry in each list is the default
 * for a freshly-placed tile.
 */
export const DATA_REPORT_TYPES_BY_SCOPE: Record<FilterScope, ReportType[]> = {
  site: ['CHARGER_ACTIVITY', 'CHARGING_TRANSACTIONS', 'METER_VALUE_INTERVAL', 'LCFS'],
  org: ['VEHICLE_ACTIVITY', 'TRIP']
}

export type DataReportIntervalPeriod = '15m' | '1h'

/** Persisted per-widget settings for a Data Table tile. */
export type DataReportSettings = {
  reportType: ReportType
  /** Meter-value-interval only; ignored by every other dataset. */
  intervalPeriod: DataReportIntervalPeriod
}

/**
 * Parses opaque persisted widget settings into a valid `DataReportSettings`.
 * Defaults the report type to the scope's first option and the interval to
 * 15 minutes, and guards against a report type that doesn't match the tile's
 * scope (e.g. settings carried over when a layout's scope changed).
 */
export function resolveDataReportSettings(settings: unknown, scope: FilterScope): DataReportSettings {
  const allowed = DATA_REPORT_TYPES_BY_SCOPE[scope]
  const raw = (settings ?? {}) as Partial<DataReportSettings>
  const reportType = raw.reportType && allowed.includes(raw.reportType) ? raw.reportType : allowed[0]!
  const intervalPeriod: DataReportIntervalPeriod = raw.intervalPeriod === '1h' ? '1h' : '15m'
  return { reportType, intervalPeriod }
}

/** Widgets that must occupy a full-width row (overviews, heatmaps, tables). */
const FULL_BLEED_WIDGET_IDS: ReadonlySet<string> = new Set([
  ENERGY_OVERVIEW_WIDGET_ID,
  ENERGY_PERIOD_COMPARISON_TABLE_WIDGET_ID,
  ENERGY_USAGE_BY_SITE_TABLE_WIDGET_ID,
  CHARGER_METRICS_HEATMAP_WIDGET_ID,
  RAW_RESULTS_TABLE_SITE_WIDGET_ID,
  VEHICLE_OVERVIEW_WIDGET_ID,
  RAW_RESULTS_TABLE_ORG_WIDGET_ID
])

export function isReportFullBleedWidgetId(widgetId: string): boolean {
  return FULL_BLEED_WIDGET_IDS.has(widgetId)
}

type HistoricalStatusMigration = {
  row: Row
  /** Connector summary that could not fit in the same row as the migrated charger tile. */
  pendingConnector?: WidgetInstance
}

function migrateHistoricalStatusSummaryRow(row: Row): HistoricalStatusMigration {
  const historicalIndex = row.slots.findIndex(
    (slot) => slot.widget?.widgetId === HISTORICAL_STATUS_SUMMARY_WIDGET_ID
  )
  if (historicalIndex < 0) return { row }

  const historicalWidget = row.slots[historicalIndex]!.widget!
  const chargerWidget: WidgetInstance = {
    ...historicalWidget,
    widgetId: CHARGER_STATUS_SUMMARY_WIDGET_ID
  }
  const connectorWidget: WidgetInstance = {
    instanceId: `${historicalWidget.instanceId}-connector`,
    widgetId: CONNECTOR_STATUS_SUMMARY_WIDGET_ID
  }

  const slotsWithCharger = row.slots.map((slot, index) =>
    index === historicalIndex ? { ...slot, widget: chargerWidget } : slot
  )

  const emptySlotIndex = slotsWithCharger.findIndex((slot) => slot.widget === null)
  if (emptySlotIndex >= 0) {
    return {
      row: {
        ...row,
        slots: slotsWithCharger.map((slot, index) =>
          index === emptySlotIndex ? { ...slot, widget: connectorWidget } : slot
        )
      }
    }
  }

  if (row.layout === 'full' && slotsWithCharger.length === 1) {
    const slotId = slotsWithCharger[0]!.slotId
    return {
      row: {
        ...row,
        layout: 'halves',
        slots: [
          { slotId, widget: chargerWidget },
          { slotId: `${slotId}-connector`, widget: connectorWidget }
        ]
      }
    }
  }

  return { row: { ...row, slots: slotsWithCharger }, pendingConnector: connectorWidget }
}

export function normalizeReportLayout(layout: DashboardLayout): DashboardLayout {
  const rows = layout.rows.flatMap((row) => {
    const { row: migratedRow, pendingConnector } = migrateHistoricalStatusSummaryRow(row)
    const nextRows: Row[] = [migratedRow]
    if (pendingConnector) {
      nextRows.push({
        rowId: `${row.rowId}-migrated-connector`,
        layout: 'halves',
        slots: [
          { slotId: `${row.rowId}-migrated-connector-slot`, widget: pendingConnector },
          { slotId: `${row.rowId}-migrated-connector-slot-empty`, widget: null }
        ]
      })
    }
    return nextRows
  })

  return normalizeFullBleedRows({ ...layout, rows }, isReportFullBleedWidgetId)
}

export type EnergyOverviewSettings = {
  mode?: 'comparison' | 'snapshot'
}

export function resolveEnergyOverviewSettings(settings: unknown): EnergyOverviewSettings {
  const raw = (settings ?? {}) as Partial<EnergyOverviewSettings>
  return { mode: raw.mode === 'snapshot' ? 'snapshot' : 'comparison' }
}

// --- Templates ---
// Standard reports are organized around the five report datasets (one per Data
// Table type) plus two chart-only "insights" reports. Site-scope reports cover
// chargers/energy/transactions; org-scope reports cover vehicles/trips.
export const CHARGER_ACTIVITY_REPORT_TEMPLATE_ID = 'charger-activity-report'
export const CHARGING_TRANSACTIONS_REPORT_TEMPLATE_ID = 'charging-transactions-report'
export const TRANSACTION_INSIGHTS_REPORT_TEMPLATE_ID = 'transaction-insights-report'
export const METER_VALUE_INTERVAL_REPORT_TEMPLATE_ID = 'meter-value-interval-report'
export const LCFS_REPORT_TEMPLATE_ID = 'lcfs-report'
export const ENERGY_INSIGHTS_REPORT_TEMPLATE_ID = 'energy-insights-report'
export const ENERGY_PERIOD_COMPARISON_REPORT_TEMPLATE_ID = 'energy-period-comparison-report'
export const ENERGY_USAGE_REPORT_TEMPLATE_ID = 'energy-usage-report'
export const VEHICLE_ACTIVITY_REPORT_TEMPLATE_ID = 'vehicle-activity-report'
export const TRIPS_REPORT_TEMPLATE_ID = 'trips-report'
export const FLEET_INSIGHTS_REPORT_TEMPLATE_ID = 'fleet-insights-report'

/** Scope of a template's layout, used as the fallback when a layout has no placed widgets. */
const TEMPLATE_SCOPE: Record<string, FilterScope> = {
  [CHARGER_ACTIVITY_REPORT_TEMPLATE_ID]: 'site',
  [CHARGING_TRANSACTIONS_REPORT_TEMPLATE_ID]: 'site',
  [TRANSACTION_INSIGHTS_REPORT_TEMPLATE_ID]: 'site',
  [METER_VALUE_INTERVAL_REPORT_TEMPLATE_ID]: 'site',
  [LCFS_REPORT_TEMPLATE_ID]: 'site',
  [ENERGY_INSIGHTS_REPORT_TEMPLATE_ID]: 'site',
  [ENERGY_PERIOD_COMPARISON_REPORT_TEMPLATE_ID]: 'site',
  [ENERGY_USAGE_REPORT_TEMPLATE_ID]: 'site',
  [VEHICLE_ACTIVITY_REPORT_TEMPLATE_ID]: 'org',
  [TRIPS_REPORT_TEMPLATE_ID]: 'org',
  [FLEET_INSIGHTS_REPORT_TEMPLATE_ID]: 'org'
}

/**
 * Derives a report layout's filter scope from its placed widgets (all widgets
 * in a report share a scope). Falls back to the template scope for an empty
 * layout, then to `'site'`.
 */
export function getReportScope(layout: DashboardLayout): FilterScope {
  for (const row of layout.rows) {
    for (const slot of row.slots) {
      if (slot.widget) {
        return REPORT_WIDGET_SCOPE[slot.widget.widgetId] ?? TEMPLATE_SCOPE[layout.id] ?? 'site'
      }
    }
  }
  return TEMPLATE_SCOPE[layout.id] ?? 'site'
}
